import { fetchAsset } from "../../core/AssetSource.js";
import { fetchApiJson } from "../../core/ApiData.js";
import { resolveCatalogKey } from "../../utils/constants.js";
import { SHOW_DEV_TOOLS, SHOW_UNRELEASED_CATALOG } from "../../config.js";

// Collection whose items are offered alongside every other collection (see _mergeSharedAccessories).
const SHARED_ACCESSORY_COLLECTION = "accessories";

export class FurnitureCatalog {
  /**
   * Load leg heights data from API
   */
  async loadLegHeightsData() {
    const data = await fetchApiJson('leg-heights', { snapshot: 'leg-heights.json' });
    if (!data) {
      console.warn('No leg heights data available');
      this.legHeightsData = {
        collection: {},
        defaults: { GLOBAL: {} }
      };
      return;
    }

    this.legHeightsData = data;

    // Set default leg height to the first available height from the API
    this.setDefaultLegHeight();
  }

  /**
   * Set default leg height from API data.
   * Only considers collections flagged `hasLegs: true` in furniture.json — collections without
   * adjustable legs (e.g. bonbon3, zip) should not seed `currentLegHeight`.
   */
  setDefaultLegHeight() {
    if (!this.legHeightsData || !this.legHeightsData.collection) {
      return;
    }

    // Collect heights only from hasLegs:true collections
    const allHeights = new Set();
    Object.entries(this.legHeightsData.collection).forEach(([collectionKey, heights]) => {
      if (!Array.isArray(heights)) return;
      // collectionKey is "<collection>-collection" (e.g. "cascais-collection")
      const collectionId = collectionKey.replace(/-collection$/, '');
      if (this.furnitureCollections?.[collectionId]?.hasLegs !== true) return;
      heights.forEach(height => allHeights.add(height));
    });

    if (allHeights.size === 0) {
      return; // Keep prior default if no eligible heights
    }

    const sortedHeights = Array.from(allHeights).sort((a, b) => parseFloat(a) - parseFloat(b));
    this.currentLegHeight = sortedHeights[0];
  }


  // Seed currentLegHeight from one item's own default, for a scene holding only that item.
  // setDefaultLegHeight() takes the minimum across every legged collection, which is 9cm short
  // for clooods/diagon. Must run before spawnFurniture, which applies currentLegHeight itself.
  seedLegHeightFor(furnitureData) {
    if (this.furnitureCollections?.[furnitureData?.collection]?.hasLegs !== true) return;
    this.currentLegHeight = this.getDefaultLegHeight(furnitureData);
  }

  /**
   * Get available leg heights for a furniture item based on its collection
   * @param {Object} furnitureData - Furniture data object
   * @returns {Array<string>} Array of available leg heights (e.g., ["8", "10.5"])
   */
  getAvailableLegHeights(furnitureData) {
    if (!this.legHeightsData || !furnitureData) {
      console.warn('No legHeightsData or furnitureData');
      return [];
    }

    // Get collection name from furniture data
    const collection = furnitureData.collection;

    if (!collection) {
      console.warn('Furniture has no collection property');
      return [];
    }

    // Look up available heights for this collection
    const collectionKey = `${collection}-collection`;

    const heights = this.legHeightsData.collection[collectionKey];

    return heights || [];
  }

  /**
   * Get default leg height for a furniture item
   * @param {Object} furnitureData - Furniture data object
   * @returns {string} Default leg height (e.g., "8")
   */
  getDefaultLegHeight(furnitureData) {
    if (!this.legHeightsData || !furnitureData) {
      return '8'; // Fallback default
    }

    const urlCode = furnitureData.urlCode;
    const defaults = this.legHeightsData.defaults?.GLOBAL || {};

    return defaults[urlCode] || '8';
  }

  // Load covers data from API (mirrors loadLegHeightsData).
  async loadCoversData() {
    const data = await fetchApiJson('product-covers', { snapshot: 'product-covers.json' });
    if (!data) console.warn('No covers data available');
    this.coversData = data || { options: { GLOBAL: {} }, defaults: { GLOBAL: {} }, categories: {} };
  }

  _currentCountry() {
    return this.app.getLocalizationManager?.()?.getCountry?.() || 'GLOBAL';
  }

  // Current-country slice of a coversData map, falling back to GLOBAL then `fallback`.
  _coverSection(map, fallback) {
    const country = this._currentCountry();
    return map?.[country] ?? map?.GLOBAL ?? fallback;
  }

  // Cover options for a product in current country, filtered by country category list ([] = none).
  getCoverOptions(urlCode) {
    if (!urlCode || !this.coversData?.options) return [];
    let available = this._coverSection(this.coversData.options, {})[urlCode] ?? [];
    const allowed = this._coverSection(this.coversData.categories, null);
    if (allowed) available = available.filter((o) => allowed.includes(o));
    return available;
  }

  // 'module'/'backrest'/null, keyed off GLOBAL options (partiallyremovable=module, nonremovable=backrest).
  getCoverGroup(urlCode) {
    const global = this.coversData?.options?.GLOBAL?.[urlCode] ?? [];
    if (global.includes('partiallyremovable')) return 'module';
    if (global.includes('nonremovable')) return 'backrest';
    return null;
  }

  // Country default cover, falling back to GLOBAL → first option → 'removable'.
  getDefaultCover(urlCode) {
    const countryDefs = this._coverSection(this.coversData?.defaults, {});
    return countryDefs[urlCode] || this.getCoverOptions(urlCode)[0] || 'removable';
  }

  // Resolve cover: valid group selection else product default; null if product has no covers.
  getCoverForProduct(urlCode) {
    const available = this.getCoverOptions(urlCode);
    if (available.length === 0) return null;
    const group = this.getCoverGroup(urlCode);
    const selected = group ? this.currentCovers[group] : null;
    if (selected && available.includes(selected)) return selected;
    return this.getDefaultCover(urlCode);
  }

  async loadFurnitureData() {
    try {
      // Load furniture data from JSON file
      const response = await fetchAsset("data/furniture.json");
      if (!response.ok) {
        throw new Error(
          `Failed to load furniture data: ${response.statusText}`,
        );
      }
      const furnitureData = await response.json();

      // Transform the data structure for backward compatibility
      this.furnitureCollections = {};
      for (const [key, collection] of Object.entries(
        furnitureData.collections,
      )) {
        // `__` = dev-only, `_` = disabled; the marker is stripped so the id stays
        // joinable with layouts.json, the leg-heights key and saved state.
        const { visible, key: collectionKey, devOnly } = resolveCatalogKey(key, SHOW_UNRELEASED_CATALOG);
        if (!visible) {
          if (SHOW_DEV_TOOLS) console.log(`Skipping ${devOnly ? "dev-only" : "disabled"} collection: ${key}`);
          continue;
        }

        // Same marker rules apply per-item, matched on `id`.
        if (collection.items) {
          collection.items = collection.items
            .map(item => {
              const resolved = resolveCatalogKey(item.id, SHOW_UNRELEASED_CATALOG);
              return resolved.visible
                ? { ...item, id: resolved.key, collection: collectionKey }
                : null;
            })
            .filter(Boolean);
        }

        // Use the original key (e.g., "cascais", "bard") instead of display name
        this.furnitureCollections[collectionKey] = collection;
      }

      this._mergeSharedAccessories();
    } catch (error) {
      console.error("Error loading furniture data:", error);
      console.error("Fetch response details:", error.message);
      // Fallback to empty collections
      this.furnitureCollections = {};
    }
  }

  /**
   * Pillows and poufs are sold alongside every collection, so the `accessories` items are
   * appended to each of the others — **by reference**, because `findCatalogItem` must resolve
   * one canonical object per accessory, and each keeps `collection: "accessories"` so its cart
   * and leg-option rules come from there rather than from whichever grid it appears in.
   */
  _mergeSharedAccessories() {
    const shared = this.furnitureCollections[SHARED_ACCESSORY_COLLECTION];
    if (!Array.isArray(shared?.items) || shared.items.length === 0) return;

    for (const [key, collection] of Object.entries(this.furnitureCollections)) {
      if (key === SHARED_ACCESSORY_COLLECTION) continue;
      if (!Array.isArray(collection?.items)) continue;
      collection.items.push(...shared.items);
    }
  }

  async loadProductUnavailability() {
    const data = await fetchApiJson('product-unavailability', {
      snapshot: 'product-unavailability.json',
    });
    if (!data) console.warn('No product unavailability data available, all products will be shown');
    this.productUnavailability = data || {};
  }

  /**
   * Returns true if the product is available in the given country.
   * @param {string} productId - The product/module id (e.g. "cascais-g1")
   * @param {string} country - ISO country code (e.g. "DE"), or "GLOBAL"
   */
  isProductAvailable(productId, country) {
    if (!this.productUnavailability || !productId) return true;
    const unavailableIn = this.productUnavailability[productId];
    if (!Array.isArray(unavailableIn) || unavailableIn.length === 0) return true;
    return !unavailableIn.includes(country);
  }

  /**
   * Find a catalog item by urlCode across all loaded collections.
   * @param {string} urlCode
   * @returns {Object|null} catalog furniture data, or null
   */
  findCatalogItemByUrlCode(urlCode) {
    return this.findCatalogItem({ urlCode });
  }

  /**
   * Find a catalog item across all loaded collections — a saved scene may hold items from a
   * collection other than the one the grid is showing. `dataId` (the unique catalog id) wins
   * over `urlCode` so same-urlCode variants (the mirrored diagon-bp-l, cascais-ppc) resolve as
   * the exact item they were saved as; `urlCode` covers layouts saved before dataId existed.
   * @param {{dataId?: string, urlCode?: string}} ref
   * @returns {Object|null} catalog furniture data, or null
   */
  findCatalogItem({ dataId, urlCode }) {
    for (const name in this.furnitureCollections) {
      const collection = this.furnitureCollections[name];
      const items = Array.isArray(collection) ? collection
        : Array.isArray(collection?.items) ? collection.items
        : Array.isArray(collection?.furniture) ? collection.furniture : null;
      if (!items) continue;
      const match = (dataId && items.find(f => f.id === dataId))
        || (urlCode && items.find(f => f.urlCode === urlCode));
      if (match) return match;
    }
    return null;
  }

  getFurnitureCollections() {
    return this.furnitureCollections;
  }

  isCartEnabledForFurniture(furnitureData) {
    if (!furnitureData || !furnitureData.collection) return true;
    const collection = this.furnitureCollections[furnitureData.collection];
    if (!collection) return true;
    return collection.cart !== false;
  }

  isLegTexturesEnabled(furnitureData) {
    if (!furnitureData || !furnitureData.collection) return true;
    const collection = this.furnitureCollections[furnitureData.collection];
    if (!collection) return true;
    return collection.legTextures !== false;
  }
}
